/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
import React, { ReactElement } from 'react';
import classNames from 'classnames';
import animate, { easeInOut } from 'components/UI_kit_3/animations/animate';
import styles from './SwitchButton.module.scss';

export type ISwitchValues<T> = Array<{
  value: T;
  title: string | ReactElement;
  disable?: boolean
}>;

interface ISwitchButtonProps<T> {
  className?: string;
  classNameBtn?: string;
  value: T;
  values: ISwitchValues<T>;
  disabled?: boolean;
  onChange: (value: T) => void;
  qaElement?: string
}

const SwitchButton = <T extends unknown>({
  className,
  classNameBtn,
  value,
  values,
  disabled,
  onChange,
  qaElement,
}: ISwitchButtonProps<T>) => {
  const switcherRef = React.useRef<HTMLSpanElement>(null);
  const activeButtonRef = React.useRef<HTMLButtonElement>(null);

  const handleSwitch = React.useCallback(
    (_value: T) => {
      onChange(_value);
    },
    [value, values],
  );

  const resizeSlider = () => {
    const startPosition = Number(switcherRef.current?.dataset.transform) || 0;
    const startWidth = switcherRef.current?.offsetWidth || 0;

    animate({
      duration: 150,
      timing: easeInOut,
      draw: (progress) => {
        if (activeButtonRef.current && switcherRef.current) {
          switcherRef.current.style.transform = `translateX(${startPosition + progress * (activeButtonRef.current.offsetLeft - startPosition)}px)`;
          switcherRef.current.dataset.transform = `${(startPosition + progress * (activeButtonRef.current.offsetLeft - startPosition))}`;

          switcherRef.current.style.width = `${startWidth + progress * (activeButtonRef.current.clientWidth - startWidth)}px`;
        }
      },
    });
  };

  React.useEffect(() => {
    resizeSlider();
  }, [value, activeButtonRef.current?.clientWidth]);

  React.useEffect(() => {
    window.addEventListener('resize', resizeSlider);
    return () => {
      window.removeEventListener('resize', resizeSlider);
    };
  }, []);

  return (
    <div className={classNames(styles.switchButton, className)} {...(qaElement ? { 'qa-element': qaElement } : {})}>
      <span ref={switcherRef} className={styles.switcher} />
      {values.map((val, index) => (
        <button
          ref={val.value === value ? activeButtonRef : undefined}
          type="button"
          key={val.title.toString()}
          className={classNames(styles.title, classNameBtn)}
          disabled={disabled || val.disable}
          onClick={() => handleSwitch(val.value)}
          data-active={val.value === value ? 'true' : 'false'}
          {...(qaElement ? { 'qa-element': `${qaElement}-${index}` } : {})}
        >
          {val.title}
        </button>
      ))}
    </div>
  );
};

SwitchButton.defaultProps = {
  className: undefined,
};

export default SwitchButton;
